import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AppUser, HeaderDetail, HeaderList, Language, ListenerDetailAction, ListenerDialogAction, ListenerHeaderDetailAction, ListenerHeaderListAction, ListenerListAction, MessageBusListener, Module, StorageKey } from '../global';
import { StorageService } from '../helper/storage.service';

const DEFAULT_LANGUAGE = 'fr';

interface AuthUser {
  username: string;
  name: string;
}

@Injectable({
  providedIn: 'root'
})

export class BusService implements OnDestroy {
  private subject$: any;
  currentUrlListDetail = '';
  hasUnsavedChanges: boolean = false;
  
  constructor(private storage: StorageService) {
    this.subject$ = new BehaviorSubject(null);
  }


  public checkUserPrivateZone = false;
  public get lastTimePrivateZoneLoginInMinutes(): any {
    let datetime = this.storage.secureSessionStorage.getItem(StorageKey.LastTimePrivateZoneLogin);
    if (datetime) {
      datetime = new Date(datetime);
    } else {
      this.lastTimePrivateZoneLoginInMinutes = new Date();
      datetime = new Date();
    }
    const difMsec: number = (new Date).getTime() - datetime.getTime();
    const mm = Math.floor(difMsec / 1000 / 60);
    return mm;
  }
  public set lastTimePrivateZoneLoginInMinutes(date: Date) {
    this.storage.secureSessionStorage.setItem(StorageKey.LastTimePrivateZoneLogin, date);
  }


  public get module(): Module {
    return this.storage.secureSessionStorage.getItem(StorageKey.Module);
  }
  public set module(mod: Module) {
    this.storage.secureSessionStorage.setItem(StorageKey.Module, mod);
  }

  public get subModule(): Module | any {
    return this.storage.secureSessionStorage.getItem(StorageKey.SubModule);
  }
  public set subModule(subMod: Module | any) {
    const subModule = subMod || '';
    this.storage.secureSessionStorage.setItem(StorageKey.SubModule, subModule);
  }

  public get initialRoute(): string {
    return this.storage.secureSessionStorage.getItem(StorageKey.InitialRoute);
  }
  public set initialRoute(initialRoute: string) {
    this.storage.secureSessionStorage.setItem(StorageKey.InitialRoute, initialRoute);
  }

  
  public get user(): AppUser {
    return this.storage.secureLocalStorage.getItem(StorageKey.User);
  }
  public set user(user: AppUser) {
    this.storage.secureLocalStorage.setItem(StorageKey.User, user);
  }



  public get currentLangTraduction(): string{
    if (this.currentLanguage === DEFAULT_LANGUAGE) {
      return 'fr';
    }
    return 'en';
  }

  public get currentLanguage(): Language {
    const lang =  this.storage.secureLocalStorage.getItem(StorageKey.CurrentLanguage);
    return lang ? lang : <any>DEFAULT_LANGUAGE;
  }
  public set currentLanguage(lang: Language) {
    this.storage.secureLocalStorage.setItem(StorageKey.CurrentLanguage, lang);
  }

  private _hasUnreadNotification: boolean = false;
  public get hasUnreadNotification(): boolean {
    return this._hasUnreadNotification;
  }
  public set hasUnreadNotification(v: boolean) {
    this._hasUnreadNotification = v;
  }

  private _unreadNotificationCount:number = 0;
  public get unreadNotificationCount(): number {
    return this._unreadNotificationCount;
  }
  public set unreadNotificationCount(c: number) {
    this._unreadNotificationCount = c;
  }


  public get apiEndpoint(): string {
    return (this.module === Module.Client ? environment.baseURLExtranetAPI : environment.baseURLCorporateAPI);
  }

  private _clientId!: number;
  public get clientId(): number {
    return this._clientId;
  }
  public set clientId(v: number) {
    this._clientId = v;
  }

  private _groupeClientId!: number | null;
  public get groupeClientId(): number | null {
    return this._groupeClientId;
  }
  public set groupeClientId(v: number | null) {
    this._groupeClientId = v;
  }

  private _clientLanguageId!: number;
  public get clientLanguageId(): number {
    return this._clientLanguageId;
  }
  public set clientLanguageId(v: number) {
    this._clientLanguageId = v;
  }

  private _clientPaysId!: number;
  public get clientPaysId(): number {
    return this._clientPaysId;
  }
  public set clientPaysId(v: number) {
    this._clientPaysId = v;
  }
  
  private _employeeId!: number;
  public get employeeId(): number {
    return this._employeeId;
  }
  public set employeeId(v: number) {
    this._employeeId = v;
  }

  private _isOperationValid = true;
  public get isOperationValid(): boolean {
    return this._isOperationValid;
  }
  public set isOperationValid(v: boolean) {
    this._isOperationValid = v;
  }

  public get initialQueryParams(): string {
    return this.storage.secureSessionStorage.getItem(StorageKey.InitialQueryParams);
  }
  public set initialQueryParams(initialQueryParams: string) {
    this.storage.secureSessionStorage.setItem(StorageKey.InitialQueryParams, initialQueryParams);
  }

  private _authenticatedUser!: AuthUser;
  public get authenticatedUser(): AuthUser {
    return this._authenticatedUser;
  }
  public set authenticatedUser(v: AuthUser) {
    this._authenticatedUser = v;
  }

  public resolvePath(path: any, obj: any){
    return path.split('.').reduce((prev: any, curr: any) => {
      return prev ? prev[curr] : null;
    }, obj || self);
  }

  getHeaderContentFilter(source: any): any {
    return  this.storage.secureLocalStorage.getItem(JSON.stringify(source + StorageKey.HeaderContentFilter));
  }
  setHeaderContentFilter(source: any, filter:  any) {
    this.storage.secureLocalStorage.setItem(JSON.stringify(source + StorageKey.HeaderContentFilter), filter);
  }


  sendMessage(actionName: string, destination: MessageBusListener, data?: any){
    this.subject$.next({listener: destination, actionName: actionName, data: data});
  }
  messages$(): Observable<any> {
    return this.subject$;
  }

  resetMessages(): void {
    this.subject$.next(EMPTY);
  }


  getFromLocalStorage(source: string, key: StorageKey | any) {
    return this.storage.secureLocalStorage.getItem(JSON.stringify(source + key));
  }
  setToLocalStorage(source: string, key: StorageKey | any, data: any) {
    this.storage.secureLocalStorage.setItem(JSON.stringify(source + key), data);
  }

  
  sendMessageToList(actionName: ListenerListAction, data?: any){
    this.subject$.next({listener: MessageBusListener.List, actionName: actionName, data: data});
  }
  sendMessageToHeaderList(message: HeaderList | ListenerHeaderListAction, data?: any){
    if (typeof(message) === 'string') {
      this.subject$.next({listener: MessageBusListener.HeaderList, actionName: message, data: data});
    } else {
      this.subject$.next({listener: MessageBusListener.HeaderList, actionName:'_changeHeaderList', data: message});
    }
  }
  sendMessageToHeaderDetail(message: HeaderDetail | ListenerHeaderDetailAction, data?: any, dialogId?: string){
    if (typeof(message) === 'string') {
      this.subject$.next({listener: MessageBusListener.HeaderDetail, actionName: message, data: data, dialogId: dialogId});
    } else {
      this.subject$.next({listener: MessageBusListener.HeaderDetail, actionName:'_changeHeaderDetail', data: message, dialogId: dialogId});
    }
  }
  sendMessageToDetail(actionName: ListenerDetailAction, data?: any){
    this.subject$.next({listener: MessageBusListener.Detail, actionName: actionName, data: data});
  }

  sendMessageToDialog(actionName: ListenerDialogAction, id: string, data?: any){
    this.subject$.next({listener: MessageBusListener.Dialog, actionName: actionName, data: data, dialogId: id});
  }
  
  ngOnDestroy(): void{
    this.subject$.complete();
  }


}
